import React, { useEffect, useState } from 'react';
import {
    Alert, Box, Button, Chip, CircularProgress, Paper, Stack, Table, TableBody,
    TableCell, TableContainer, TableHead, TableRow, Typography,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { ExpenseClaimService } from '../../services/expense-claim.service';
import { ExpenseClaimDTO } from '../../interfaces/expense.interface';
import { CLAIM_STATUS_COLOR, claimRef, claimStatusLabel, formatAmount } from '../../utils/expenseDisplay';
import { formatMoney } from '../../utils/format';
import { formatDate } from '../../utils/dateUtils';
import { departmentNameOf, useDepartmentOptions } from '../costcentre/DepartmentPicker'; // polish 4
import { projectLabel, projectTagLabel, useProjectOptions } from '../project/ProjectSelect'; // P1-A project
import { AuthService } from '../../services/auth.service';
import { useToast } from '../../context/ToastContext';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import ExpensePayDialog from './ExpensePayDialog';
import ExpenseReceiptLink from './ExpenseReceiptLink';
import { ExpenseAdvanceService } from '../../services/expense-advance.service';
import { ExpenseAdvanceDTO } from '../../interfaces/expense.interface';
import { advanceRef, advanceStatusLabel } from '../../utils/expenseDisplay';

const ExpenseClaimDetail: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const toast = useToast();
    const departmentOptions = useDepartmentOptions(AuthService.getSelectedInstitution()); // polish 4
    const projectOptions = useProjectOptions(AuthService.getSelectedInstitution()); // P1-A project
    const projectName = (id?: string | null, code?: string | null, name?: string | null) => { const p = id ? projectOptions.find(o => o.id === id) : undefined; return p ? projectLabel(p) : (code ? projectTagLabel(code, name) : (id ? id.slice(0, 8) : '—')); };
    const [claim, setClaim] = useState<ExpenseClaimDTO | null>(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [payDialogOpen, setPayDialogOpen] = useState(false);
    const canPay = usePermission(PERMISSIONS.EXPENSE_CLAIM_PAY);
    const [advance, setAdvance] = useState<ExpenseAdvanceDTO | null>(null);

    useEffect(() => {
        if (id) load(id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    // The advance read is limited to its requester / finance roles; other viewers keep the short ref.
    useEffect(() => {
        setAdvance(null);
        const advId = claim?.linkedAdvanceId;
        if (!advId) return;
        let cancelled = false;
        ExpenseAdvanceService.getById(advId)
            .then(a => { if (!cancelled) setAdvance(a); })
            .catch(() => { /* fall back to the short reference */ });
        return () => { cancelled = true; };
    }, [claim?.linkedAdvanceId]);

    const load = async (claimId: string) => {
        setLoading(true);
        try {
            setClaim(await ExpenseClaimService.getById(claimId));
        } catch (e: any) {
            toast.showApiError(e, 'Failed to load claim');
        } finally {
            setLoading(false);
        }
    };

    const handleAction = async (
        action: 'submit' | 'recall' | 'delete',
        confirmMsg?: string,
    ) => {
        if (!id) return;
        if (confirmMsg && !window.confirm(confirmMsg)) return;
        setBusy(true);
        try {
            if (action === 'submit') {
                const next = await ExpenseClaimService.submit(id);
                setClaim(next);
                toast.showSuccess('Claim submitted');
            } else if (action === 'recall') {
                const next = await ExpenseClaimService.recall(id);
                setClaim(next);
                toast.showSuccess('Claim recalled');
            } else if (action === 'delete') {
                await ExpenseClaimService.softDelete(id);
                toast.showSuccess('Draft deleted');
                navigate('/expense/my-claims');
                return;
            }
        } catch (e: any) {
            const data = e?.response?.data;
            const msg = data?.message ?? (typeof data === 'string' ? data : null);
            if (msg) toast.showError(msg);
            else toast.showApiError(e, 'Action failed');
        } finally {
            setBusy(false);
        }
    };

    if (loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
    }
    if (!claim) return null;

    const isDraft = claim.status === 'DRAFT';
    // A draft is simply deleted; recall only makes sense once the claim is with an approver.
    const isRecallable = claim.status === 'SUBMITTED';
    const canPayNow = canPay && claim.status === 'APPROVED' && claim.reimbursementMethod === 'STANDALONE';

    return (
        <Box sx={{ p: { xs: 1.5, sm: 3 }, maxWidth: 1100 }} data-help-page="expense.claimDetail.page">
            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                <Typography variant="h5">{claim.title}</Typography>
                <Typography variant="body2" color="text.secondary">{claimRef(claim.id)}</Typography>
                <Chip label={claimStatusLabel(claim.status)} color={CLAIM_STATUS_COLOR[claim.status]} />
            </Stack>

            <Paper sx={{ p: 2, mb: 2 }}>
                <Stack direction="row" useFlexGap flexWrap="wrap" spacing={4}>
                    <Box>
                        <Typography variant="caption" color="text.secondary">Period</Typography>
                        <Typography>{formatDate(claim.periodFrom)} → {formatDate(claim.periodTo)}</Typography>
                    </Box>
                    <Box>
                        <Typography variant="caption" color="text.secondary">Total</Typography>
                        <Typography variant="h6">{formatMoney(claim.totalAmount, 'BDT')}</Typography>
                    </Box>
                    {claim.currentApprovalTier != null && (
                        <Box>
                            <Typography variant="caption" color="text.secondary">Current tier</Typography>
                            <Typography>{claim.currentApprovalTier}</Typography>
                        </Box>
                    )}
                    {claim.linkedAdvanceId && (
                        <Box>
                            <Typography variant="caption" color="text.secondary">Linked advance</Typography>
                            <Typography>
                                {advance
                                    ? `${formatMoney(advance.amount, 'BDT')} — ${advance.reason}`
                                    : advanceRef(claim.linkedAdvanceId)}
                            </Typography>
                            {advance && (
                                <Typography variant="caption" color="text.secondary">
                                    {advanceRef(advance.id)} · {advanceStatusLabel(advance.status)}
                                    {advance.paidAt ? ` · paid ${formatDate(advance.paidAt)}` : ''}
                                </Typography>
                            )}
                        </Box>
                    )}
                    {claim.netReimbursable != null && (
                        <Box>
                            <Typography variant="caption" color="text.secondary">Net reimbursable</Typography>
                            <Typography variant="h6">{formatMoney(claim.netReimbursable, 'BDT')}</Typography>
                        </Box>
                    )}
                </Stack>
            </Paper>

            {/* W3-U */}
            {(claim.limitWarnings ?? []).length > 0 && (
                <Alert severity="warning" sx={{ mb: 2 }} data-testid="limit-warnings">
                    {(claim.limitWarnings ?? []).map((w, i) => <div key={i}>{w.message}</div>)}
                </Alert>
            )}

            <TableContainer component={Paper} data-help-section="expense.claimDetail.lines">
                <Table size="small" sx={{ '& th, & td': { px: { xs: 0.75, sm: 2 } }, '& td:first-of-type': { whiteSpace: 'nowrap' } }}>
                    <TableHead>
                        <TableRow>
                            <TableCell data-help="expense.claimLines.col.date">Date</TableCell>
                            <TableCell data-help="expense.claimLines.col.category">Category</TableCell>
                            <TableCell data-help="expense.claimLines.col.description">Description</TableCell>
                            {claim.lines.some(l => l.departmentId) && <TableCell data-help="expense.claimLines.col.department">Department</TableCell>}
                                {claim.lines.some(l => l.projectId) && <TableCell data-help="expense.claimLines.col.project">Project</TableCell>}
                            <TableCell align="right" data-help="expense.claimLines.col.amount">Amount</TableCell>
                            <TableCell data-help="expense.claimLines.col.receipt">Receipt</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {claim.lines.map(l => (
                            <TableRow key={l.id}>
                                <TableCell data-help="expense.claimLines.col.date">{l.expenseDate ? formatDate(l.expenseDate) : '—'}</TableCell>
                                <TableCell data-help="expense.claimLines.col.category">{l.categoryName ?? '—'}</TableCell>
                                <TableCell data-help="expense.claimLines.col.description" sx={{ whiteSpace: 'normal', minWidth: 140 }}>{l.description ?? '—'}
                                    {l.lineKind && l.lineKind !== 'STANDARD' && (
                                        <Typography variant="caption" color="text.secondary" display="block">
                                            {l.lineKind === 'PER_DIEM' ? `Per diem: ${l.quantity ?? ''} day(s) x ${l.rate ?? ''}`
                                                : l.lineKind === 'MILEAGE' ? `Mileage: ${l.quantity ?? ''} km x ${l.rate ?? ''} ${l.vehicleType ?? ''} ${l.routeText ?? ''}`
                                                    : 'Paid by corporate card'}
                                        </Typography>
                                    )}</TableCell>
                                {claim.lines.some(x => x.departmentId) && <TableCell data-testid="line-department" data-help="expense.claimLines.col.department">{departmentNameOf(departmentOptions, l.departmentId)}</TableCell>}
                                    {claim.lines.some(x => x.projectId) && <TableCell data-testid="line-project" data-help="expense.claimLines.col.project">{projectName(l.projectId, l.projectCode, l.projectName)}</TableCell>}
                                <TableCell align="right" data-help="expense.claimLines.col.amount">{formatAmount(l.amount)}</TableCell>
                                <TableCell data-help="expense.claimLines.col.receipt"><ExpenseReceiptLink claimId={claim.id} receiptId={l.receiptDocumentId} /></TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Stack direction="row" spacing={2} useFlexGap sx={{ mt: 3, flexWrap: 'wrap' }}>
                <Button variant="outlined" data-help="expense.claimDetail.back" onClick={() => navigate('/expense/my-claims')}>Back</Button>
                {isDraft && (
                    <Button
                        variant="outlined" data-help="expense.claimDetail.edit"
                        onClick={() => navigate(`/expense/my-claims/${claim.id}/edit`)}
                    >
                        Edit
                    </Button>
                )}
                {isDraft && (
                    <Button
                        variant="contained" data-help="expense.claimDetail.submit"
                        onClick={() => handleAction('submit')}
                        disabled={busy}
                    >
                        Submit
                    </Button>
                )}
                {isRecallable && (
                    <Button
                        color="warning" data-help="expense.claimDetail.recall"
                        variant="outlined"
                        onClick={() => handleAction('recall', 'Recall this claim? It will be cancelled.')}
                        disabled={busy}
                    >
                        Recall
                    </Button>
                )}
                {isDraft && (
                    <Button
                        color="error" data-help="expense.claimDetail.delete"
                        variant="outlined"
                        onClick={() => handleAction('delete', 'Delete this draft? This cannot be undone.')}
                        disabled={busy}
                    >
                        Delete
                    </Button>
                )}
                {canPayNow && (
                    <Button
                        color="success" data-help="expense.claimDetail.payNow"
                        variant="contained"
                        onClick={() => setPayDialogOpen(true)}
                        disabled={busy}
                    >
                        Pay now
                    </Button>
                )}
            </Stack>
            {claim && (
                <ExpensePayDialog
                    open={payDialogOpen}
                    claim={claim}
                    onClose={() => setPayDialogOpen(false)}
                    onPaid={next => setClaim(next)}
                />
            )}
        </Box>
    );
};

export default ExpenseClaimDetail;
